import { useTheme } from '@mui/material';
import ReactEcharts from 'echarts-for-react';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeInTimeZone } from 'utils/date.utils';
import { Statistics, StatisticsKeys } from './campaign-statistics-types';

interface Props {
  funnelStatistics: Statistics | undefined;
  timeZone: string | undefined;
  /** Klick auf einen Balken: Tag (`YYYY-MM-DD`, wie in `dates`) und Schritt (z. B. `sendConnectionRequest`). */
  onBarClick?: (bar: { dateKey: string; stepName: string }) => void;
}

const CampaignStepBarChart: React.FC<Props> = ({ funnelStatistics, timeZone, onBarClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const excludedKeys = ['dates', 'negativeOutcomes', 'positiveOutcomes'];
  const includedSteps = funnelStatistics ? (Object.keys(funnelStatistics).filter((key) => !excludedKeys.includes(key)) as StatisticsKeys[]) : [];

  const myTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dates = funnelStatistics?.dates?.map((item: string) => formatDateTimeInTimeZone(item, 'dd MMM', timeZone || myTimeZone));
  const performance = _.omit(funnelStatistics, 'dates', 'negativeOutcomes', 'positiveOutcomes') as Record<string, number[]>;

  const colorMap: Record<string, string> = {
    sendConnectionRequest: '#8B65D5',
    sendFollowUp: '#4392F1',
    sendMail: '#5F71E7',
    sendMailWithContext: '#A66DF4',
    sendInMail: '#547BFB',
    sendInMailWithContext: '#7EAAFF',
    sendEmail: '#9E76F2',
    sendFollowUpEmail: '#BF62E1',
    sendEmailWithContext: '#477AED',
    sendCallTranscript: '#685CEB',
  };

  /** Vertical brand gradient (bottom -> top), like in the design. */
  const barGradient = (baseColor: string) => ({
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: theme.palette.secondary.main },
      { offset: 1, color: baseColor },
    ],
  });

  const seriesData = includedSteps.map((stepName) => {
    const baseColor = colorMap[stepName] || theme.palette.primary.main;

    return {
      name: t(stepName),
      type: 'bar',
      data: performance[stepName],
      baseColor,
      itemStyle: {
        color: barGradient(baseColor),
        borderRadius: [8, 8, 0, 0],
      },
      barGap: '10%',
      label: {
        show: true,
        position: 'inside',
        color: '#fff',
        formatter: (params: any) => (params.value === 0 ? '' : params.value),
      },
    };
  });

  const option = {
    color: seriesData.map((s: any) => s.baseColor),
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    grid: {
      top: '15%',
      left: '1%',
      right: '1%',
      bottom: '2%',
      containLabel: true,
    },
    legend: {
      show: true,
      left: '1%',
      top: '0%',
      orient: 'horizontal',
      icon: 'circle',
      itemGap: 25,
      itemWidth: 12,
      itemHeight: 12,
      formatter: (name: string) => `  ${name}`,
      textStyle: {
        color: theme.palette.text.primary,
        fontSize: 14,
      },
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLine: {
        lineStyle: { color: theme.palette.text.primary },
      },
      axisLabel: {
        color: theme.palette.text.secondary,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      axisLine: {
        lineStyle: { color: theme.palette.text.primary },
      },
      axisLabel: {
        color: theme.palette.text.secondary,
      },
      splitLine: {
        show: true,
        lineStyle: { color: theme.palette.divider },
      },
    },
    series: seriesData,
  };

  const onEvents = {
    click: (params: { componentType?: string; seriesIndex?: number; dataIndex?: number; value?: number }) => {
      if (!onBarClick || params.componentType !== 'series' || !params.value) return;
      const dateKey = funnelStatistics?.dates?.[params.dataIndex ?? -1];
      const stepName = includedSteps[params.seriesIndex ?? -1];
      if (dateKey && stepName) onBarClick({ dateKey, stepName });
    },
  };

  return (
    <ReactEcharts
      option={option}
      onEvents={onEvents}
      style={{ height: '400px', width: '100%', cursor: onBarClick ? 'pointer' : undefined }}
    />
  );
};

export default CampaignStepBarChart;
